import uuid from 'js/src/libs/uuid';

/**
 * This class helps keep track of time if you need to pause/resume. If you don't, please use timer.js instead.
 * Each start/pause tuple creates a new lap, and all laps are stored internally as this._lapTimes. This allows
 * us to calculate the total cumulative duration, which is retrieved as this.totalDuration.
 *
 * An example of this is recording, where a user may start a recording, pause at any time, and resume. When
 * we resume, we need to ignore the time tracking when paused was clicked and pick up where we left off.
 *
 */
const START_KEY = 'startTime';
const END_KEY = 'endTime';
class Stopwatch {
    // each lap stores a tuple of start/pause times, lapTimes can then be used to calculate the total duration
    _lapTimes: {
        [lap_id: string]: {
            [START_KEY]: number;
            [END_KEY]?: number;
        };
    };
    // current lap: when start() is called but we haven't paused/stopped yet
    _currentLapId: string | null;

    constructor() {
        this._lapTimes = {}; // each lap is an object { startTime: number in ms, endTime: number in ms }
        this._currentLapId = null;
    }

    start() {
        // start a new session if it hasn't started already
        if (!this._currentLapId) {
            this._currentLapId = uuid();
        }

        this._lapTimes[this._currentLapId] = {
            [START_KEY]: new Date().getTime(),
            [END_KEY]: undefined,
        };
    }

    pause() {
        if (!this._currentLapId || !this._lapTimes[this._currentLapId]) {
            // can't pause if there isn't a session running
            return;
        }

        this._lapTimes[this._currentLapId][END_KEY] = new Date().getTime();
        this._currentLapId = null;
    }

    get totalDuration() {
        const timeKeys = Object.keys(this._lapTimes);
        if (timeKeys.length <= 0) {
            return 0;
        }

        // we just started but we're also trying to read the current lap's time
        if (timeKeys.length === 1 && this._currentLapId) {
            const now = new Date().getTime();
            return now - this._lapTimes[this._currentLapId][START_KEY];
        }

        // iterate each start/end tuple via key, compute the duration, sum them together
        const totalDuration = timeKeys.reduce((duration, lapKey) => {
            const startTime = this._lapTimes[lapKey][START_KEY];
            const endTime = this._lapTimes[lapKey][END_KEY];
            if (!startTime) {
                return duration; // skip this tuple if there isn't a start or end, data corrupted
            }

            if (!endTime) {
                const currentLapDuration = new Date().getTime() - startTime;
                return duration + currentLapDuration;
            }

            const lapDuration = endTime - startTime;
            return duration + lapDuration;
        }, 0);

        return totalDuration;
    }

    reset() {
        this._lapTimes = {};
        this._currentLapId = null;
    }

    stop(shouldReset = false) {
        this.pause(); // end the last session
        const saveDurationBeforeClear = this.totalDuration;
        shouldReset && this.reset();
        return saveDurationBeforeClear;
    }
}

export default Stopwatch;
